import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfigService } from '@markant-platform/shared/data-access';
import {
  Recipient,
  UserNotification,
  UserNotificationsService,
} from '@markant-platform/user-notifications/data-access';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  interval,
  map,
  Observable,
  takeWhile,
  throttleTime,
  timer,
} from 'rxjs';
import { MarkantElementsModule, MarkantPopover } from '@markant/elements-angular';
import { SharedUtilPipesModule } from '@markant-platform/shared/util-pipes';
import { CdkScrollable, CdkVirtualScrollViewport, ScrollDispatcher, ScrollingModule } from '@angular/cdk/scrolling';
import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'markant-platform-user-notifications',
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    TranslocoModule,
    SharedUtilPipesModule,
    ScrollingModule,
    ExperimentalScrollingModule,
  ],
  templateUrl: 'user-notifications.component.html',
  styleUrls: ['user-notifications.component.scss'],
  providers: [DatePipe],
})
export class UserNotificationsComponent implements OnInit {
  @ViewChild(CdkVirtualScrollViewport) virtualScroll!: CdkVirtualScrollViewport;
  @ViewChild(MarkantPopover, { static: true }) popover!: MarkantPopover;

  readonly defaultScrollHeight = 350;
  readonly overflowAuthorNameLength = 26;

  userNotifications$ = new BehaviorSubject<UserNotification[]>([]);
  preloadedNotifications: UserNotification[] = [];
  unreadOnlyEnabled = false;
  reloadContents$ = new BehaviorSubject<boolean>(false); // Re-rendering by opening popover
  loadingNext$ = new BehaviorSubject<boolean>(false); // State of api call
  appendingNext$ = new BehaviorSubject<boolean>(false); // State of appending result
  loadedFirstResult$ = new BehaviorSubject<boolean>(false);
  loadedAllResult$ = new BehaviorSubject<boolean>(false);
  scrolledObservable$ = new Observable<CdkScrollable | void>();
  scrollHeight = this.defaultScrollHeight;
  showPopover = false;

  $unreadNotificationCount = signal<number>(0);
  $notificationCount = computed(() => (this.$unreadNotificationCount() > 99 ? '99+' : this.$unreadNotificationCount()));
  $notificationsWidthSmall = computed(
    () => this.$unreadNotificationCount() > 9 && this.$unreadNotificationCount() <= 99
  );
  $notificationsWidthBig = computed(() => this.$unreadNotificationCount() > 99);

  private readonly defaultNotificationInterval = 300000; // default interval is 5 minutes
  private readonly userNotificationsInterval = this.getNotificationInterval();
  private readonly initialLoadingTimestamp = new Date().toISOString(); // when the component is initialized
  private readonly oldestPossibleTimestamp = '2020-01-01T00:00:00Z';
  private lastNewLoadingTimestamp = this.initialLoadingTimestamp; // when last time new notifications were loaded

  private pageIndex = 0;
  private readonly pageSize = 10; // Number of notifications loaded by single request
  private hasNext = true;

  private readonly userNotificationsService: UserNotificationsService = inject(UserNotificationsService);
  private readonly router: Router = inject(Router);
  private readonly translocoService: TranslocoService = inject(TranslocoService);
  private readonly scrollDispatcher: ScrollDispatcher = inject(ScrollDispatcher);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  async ngOnInit(): Promise<void> {
    this.translocoService.langChanges$
      .pipe(distinctUntilChanged(), debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetNotifications());

    this.loadedFirstResult$
      .pipe(
        filter(bool => bool),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => setTimeout(() => this.setScrollHeight()));

    this.appendingNext$
      .pipe(
        filter(bool => bool),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() =>
        interval(300)
          .pipe(
            takeWhile(() => this.appendingNext$.value),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(() => this.appendPreloadedNotificationsAndLoadNext())
      );

    this.scrolledObservable$ = this.scrollDispatcher.scrolled();

    this.scrolledObservable$
      .pipe(
        filter(() => this.virtualScroll?.measureScrollOffset('bottom') === 0 && !this.loadedAllResult$.value),
        throttleTime(300),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.appendingNext$.next(true));

    this.scrolledObservable$
      .pipe(throttleTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.virtualScroll?.checkViewportSize());

    await this.subscribeNotificationAndUnreadCount();
    await this.getUnreadUserNotificationsCount();
  }

  async togglePopover(): Promise<void> {
    this.showPopover = !this.showPopover;
    if (this.showPopover) {
      await this.openUserNotifications();
    }
  }

  closePopover(): void {
    this.showPopover = false;
  }

  appendPreloadedNotificationsAndLoadNext(): void {
    if (this.preloadedNotifications.length > 0) {
      this.appendNotificationsToSubject(this.preloadedNotifications);
      this.preloadedNotifications = [];
      if (!this.hasNext) {
        this.loadedAllResult$.next(true);
      }

      if (this.hasNext && !this.loadingNext$.value) {
        this.pageIndex++;
        this.loadNextNotifications(true);
      }

      this.appendingNext$.next(false);
      setTimeout(() => this.virtualScroll.checkViewportSize(), 300);
    }
  }

  resetNotifications(): void {
    this.pageIndex = 0;
    this.userNotifications$.next([]);
    this.preloadedNotifications = [];
    this.loadedFirstResult$.next(false);
    this.loadedAllResult$.next(false);
    this.loadNextNotifications();
  }

  async subscribeNotificationAndUnreadCount(): Promise<void> {
    interval(this.userNotificationsInterval)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getUnreadUserNotificationsCount();
        this.loadNewComingNotifications();
      });
  }

  async openUserNotifications(): Promise<void> {
    await this.reloadContentForRendering();
    setTimeout(() => this.setScrollHeight());
  }

  // It is a workaround to avoid rendering problem that occur after redirecting the page
  async reloadContentForRendering(): Promise<void> {
    const oneTickObservable = timer(0);
    const setToTrue = oneTickObservable.pipe(map(() => this.reloadContents$.next(true)));
    const setToFalse = oneTickObservable.pipe(map(() => this.reloadContents$.next(false)));
    await firstValueFrom(combineLatest([setToFalse, setToTrue]));
  }

  toggleNotifications(): void {
    this.unreadOnlyEnabled = !this.unreadOnlyEnabled;
    this.resetNotifications();
  }

  loadNextNotifications(preload = false): void {
    this.loadingNext$.next(true);
    this.userNotificationsService
      .getUserNotifications(
        this.pageIndex,
        this.pageSize,
        this.unreadOnlyEnabled,
        this.oldestPossibleTimestamp,
        this.initialLoadingTimestamp
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(page => {
        this.hasNext = page.hasNext;

        if (preload) {
          // By preloading save loaded notifications in the array temporarily. They are added to the subject by scrolling.
          this.preloadedNotifications = page.result;
        } else {
          // By non-preloading add loaded notification directly to the subject
          this.appendNotificationsToSubject(page.result);
          this.loadedFirstResult$.next(true);
          if (this.hasNext) {
            this.pageIndex++;
            this.loadNextNotifications(true);
          } else {
            this.loadedAllResult$.next(true);
          }
        }

        this.loadingNext$.next(false);
      });
  }

  loadNewComingNotifications(): void {
    const currentTimestamp = new Date().toISOString();

    this.userNotificationsService
      .getUserNotifications(0, 10, this.unreadOnlyEnabled, this.lastNewLoadingTimestamp, currentTimestamp)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(page => {
        this.lastNewLoadingTimestamp = currentTimestamp;
        this.appendNotificationsToSubject(page.result, false);
      });
  }

  appendNotificationsToSubject(appendedNotifications: UserNotification[], appendToBottom = true): void {
    const mergedNotifications = appendToBottom
      ? [...this.userNotifications$.value, ...appendedNotifications]
      : [...appendedNotifications, ...this.userNotifications$.value];

    this.userNotifications$.next(mergedNotifications);
  }

  async navigateToNewsItem(selectedNotification: UserNotification): Promise<void> {
    await this.markNotificationAsRead(selectedNotification.id);

    if (selectedNotification.link !== '' && selectedNotification.link !== null) {
      if (selectedNotification.link.includes('/platform')) {
        const internalUrl = selectedNotification.link.split('/platform')[1] || '';

        // trigger invisible redirect to root before navigating to internal url to force reload of page
        await this.router.navigateByUrl('/', { skipLocationChange: true }).then(async () => {
          await this.router.navigateByUrl(internalUrl);
        });
      }
    }
  }

  async getUnreadUserNotificationsCount(): Promise<void> {
    this.$unreadNotificationCount.set(
      await firstValueFrom(this.userNotificationsService.getUnreadUserNotificationsCount())
    );
  }

  //Mark SINGLE user notification as read
  async markNotificationAsRead(notificationId: string): Promise<void> {
    await firstValueFrom(this.userNotificationsService.markUserNotificationAsRead(notificationId));
    this.markLoadedNotificationsAsRead([notificationId]);
    this.$unreadNotificationCount.update(value => value--);
  }

  //Mark ALL user notification as read
  async markAllUserNotificationsAsRead(): Promise<void> {
    await firstValueFrom(this.userNotificationsService.markAllUserNotificationAsRead());
    this.markLoadedNotificationsAsRead();
    this.$unreadNotificationCount.set(0);
  }

  // if no argument mark set, all notification will be marked as read
  markLoadedNotificationsAsRead(ids?: string[]): void {
    const updated = this.userNotifications$.value.map(notification =>
      !ids || ids.includes(notification.id) ? this.markLoadedSingleNotificationAsRead(notification) : notification
    );
    this.userNotifications$.next(updated);

    if (!ids) {
      this.preloadedNotifications = this.preloadedNotifications.map(notification =>
        this.markLoadedSingleNotificationAsRead(notification)
      );
    }
  }

  markLoadedSingleNotificationAsRead(notification: UserNotification): UserNotification {
    const recipient = notification.recipients[0];
    if (recipient) {
      recipient.read = true;
    } else {
      notification = {
        ...notification,
        recipients: [{ read: true } as Recipient],
      };
    }
    return notification;
  }

  //Fire delete notification action
  async deleteNotification(selectedNotification: UserNotification): Promise<void> {
    await firstValueFrom(this.userNotificationsService.deleteUserNotifications([selectedNotification.id]));
    const notifications = this.userNotifications$.value.filter(
      notification => notification.id !== selectedNotification.id
    );
    return this.userNotifications$.next(notifications);
  }

  getNotificationInterval(): number {
    const notificationInterval = AppConfigService.appConfiguration.notificationCheckInterval;
    if (!notificationInterval) {
      return this.defaultNotificationInterval;
    }

    if (isNaN(notificationInterval)) {
      return this.defaultNotificationInterval;
    }

    return +notificationInterval;
  }

  setScrollHeight(): void {
    if (this.virtualScroll) {
      const contentSize = this.virtualScroll.measureRenderedContentSize();
      this.scrollHeight = contentSize < this.defaultScrollHeight ? contentSize : this.defaultScrollHeight;
    }
  }
}
